import { getJwtToken } from '../config/cognito'
import { ApiError } from '../config/api'

const API_BASE_URL = (process.env.REACT_APP_API_URL || '').replace(/\/+$/, '')

// ---------------------------------------------------------------------------
// Structural mirror of `WpsTemplateData` in
// `server/src/pdf/wpsDataMapper.ts`. Keeping the shape in two places
// avoids a build-time cross-package dependency; any server change must
// be mirrored here.
// ---------------------------------------------------------------------------

export interface WpsPreviewIdentifiers {
  wpsNumber: string
  revision: string
  sheetOf: string
}

export interface WpsPreviewKvCell {
  label: string
  value: string
  /**
   * Optional vertical row-span (defaults to 1). When `> 1`, the cell
   * stretches to fill `rowSpan` consecutive rows; the cell at the
   * same column on subsequent rows is `null` and skipped by the
   * column-major renderer. Mirrors the `G5:H6` / `I5:L6` merge for
   * the Equipment field in `WPS-pdf.xlsx`.
   */
  rowSpan?: number
}

/**
 * XLSX-faithful 3-column top-meta row (slice 02 of
 * wps-pdf-xlsx-faithful). The middle slot is `null` on the last row
 * to represent the merged-blank cell in XLSX K6:L6 — that area is
 * "filled" visually by the Equipment cell above carrying `rowSpan: 2`.
 */
export type WpsPreviewTopMetaRow = [
  WpsPreviewKvCell,
  WpsPreviewKvCell | null,
  WpsPreviewKvCell
]

export interface WpsPreviewBaseMaterial {
  materialSpec: string
  typeOrGrade: string
  /** Combined hyphen-joined range (back-compat). */
  nominalDiameterRange: string
  /** Min cell for the XLSX-faithful `[label] [min] to [max]` row layout. */
  nominalDiameterMin: string
  /** Max cell for the XLSX-faithful `[label] [min] to [max]` row layout. */
  nominalDiameterMax: string
  /** Combined hyphen-joined range (back-compat). */
  wallThicknessRange: string
  wallThicknessMin: string
  wallThicknessMax: string
  slabSource: string
  plateSource: string
  pipeManufacturer: string
}

export interface WpsPreviewJointBevel {
  jointType: string
  dimensions: Array<{ key: string; label: string; value: string }>
  backingRequirements: string
  backingMaterial: string
  imageUrl: string
  weldId?: string
  weldNumber?: string
}

export interface WpsPreviewClampPreheat {
  clamp: { lineUpClamp: string; removal: string }
  preheat: {
    preheatMin: string
    interpassMin: string
    interpassMax: string
    heatingMethod: string
    controlMethod: string
    notes: string[]
  }
  welders: { minRootCount: string; minRemainingCount: string }
  weldTimes: { maxRootToHot: string; maxHotToFill: string; passNotesPdf: string }
}

export interface WpsPreviewWeldPassSequence {
  rootType: 'internal' | 'external' | ''
  capCount: number | null
  fillCount: number | null
  imageUrl: string
  weldId?: string
  weldNumber?: string
}

export type WpsPreviewWeldingParameterColumnId =
  | 'root'
  | 'hotPass'
  | 'fills'
  | 'splitCap'
  | 'rootMisfire'

export interface WpsPreviewWeldingParameterColumn {
  id: WpsPreviewWeldingParameterColumnId
  label: string
}

export interface WpsPreviewWeldingParameterRow {
  label: string
  unit?: string
  /**
   * Stable parameter key for click-to-edit (slice 05 of
   * `wps-preview-pixel-parity`). Mirrors the server-side
   * `WpsWeldingParameterRow.key` field; matches the destination
   * `ParameterMatrix` column key so the WPS Preview canvas can build
   * a `welding-parameter-cell` descriptor whose `focusField` aligns
   * with `data-focus-field-cell="<key>:<pass>"` on the parameters
   * page. Optional for back-compat with reports persisted before
   * this slice.
   */
  key?: string
  isRange: boolean
  values: Array<string | { min: string; max: string }>
}

export interface WpsPreviewWeldingParameters {
  columns: WpsPreviewWeldingParameterColumn[]
  rows: WpsPreviewWeldingParameterRow[]
}

export interface WpsPreviewCleaningPostWeld {
  cleaningGrindingNotes: string
  postHeating: string
  pwht: string
  controlledCoolingMethod: string
}

export interface WpsPreviewNote {
  id: string
  body: string
}

export interface WpsPreviewApprovalColumn {
  /** Canonical contractor/endClient/crc or any extra imported/custom slot id. */
  id: string
  label: string
  name: string
  /**
   * Optional printed job title (slice 07 of wps-pdf-xlsx-faithful).
   * Defaults to empty string for records that pre-date the field.
   */
  title?: string
  date?: string
  signatureImage?: string
  stampImage?: string
  memberId?: string
}

export interface WpsPreviewTemplateData {
  identifiers: WpsPreviewIdentifiers
  topMeta: WpsPreviewTopMetaRow[]
  baseMaterial: WpsPreviewBaseMaterial
  jointBevel: WpsPreviewJointBevel
  /** Multi-weld stacked joint/bevel sections when present. */
  jointBevels?: WpsPreviewJointBevel[]
  clampPreheat: WpsPreviewClampPreheat
  weldPassSequence: WpsPreviewWeldPassSequence
  weldPassSequences?: WpsPreviewWeldPassSequence[]
  weldingParameters: WpsPreviewWeldingParameters
  cleaningPostWeld: WpsPreviewCleaningPostWeld
  notes: WpsPreviewNote[]
  approvals: WpsPreviewApprovalColumn[]
  disclaimer: string
  generatedAt: string
}

export interface WpsPreviewResponse {
  data: WpsPreviewTemplateData
  version: number
}

/**
 * Fetch the same payload the WPS PDF renderer consumes, so the
 * preview canvas can render the PDF layout 1:1.
 *
 * Uses a hand-rolled fetch (rather than `apiFetch`) so the caller can
 * cross-check the `ETag` header against the JSON `version` field —
 * the two should coincide.
 */
export interface WpsPreviewScope {
  weldIds?: string[]
  pqrNumbers?: string[]
}

export async function fetchWpsPreviewData(
  projectId: string,
  pipeSetId: string,
  scope?: WpsPreviewScope | string | string[] | null
): Promise<WpsPreviewResponse> {
  const token = await getJwtToken()
  if (!token) {
    throw new ApiError(401, 'Not authenticated')
  }

  const base = `${API_BASE_URL}/api/projects/${projectId}/pipe-sets/${pipeSetId}/reports/wps-preview`
  const weldIds = (() => {
    if (scope && typeof scope === 'object' && !Array.isArray(scope)) {
      return (scope.weldIds ?? [])
        .map((w) => (typeof w === 'string' ? w.trim() : ''))
        .filter((w) => w.length > 0)
    }
    if (Array.isArray(scope)) {
      return scope.map((w) => (typeof w === 'string' ? w.trim() : '')).filter((w) => w.length > 0)
    }
    if (scope && typeof scope === 'string' && scope.trim()) {
      return [scope.trim()]
    }
    return []
  })()
  const pqrNumbers =
    scope && typeof scope === 'object' && !Array.isArray(scope)
      ? (scope.pqrNumbers ?? [])
          .map((p) => (typeof p === 'string' ? p.trim() : ''))
          .filter((p) => p.length > 0)
      : []
  const params = new URLSearchParams()
  if (weldIds.length > 1) {
    params.set('weldIds', weldIds.join(','))
  } else if (weldIds.length === 1) {
    params.set('weldId', weldIds[0])
  }
  if (pqrNumbers.length > 0) {
    params.set('pqrNumbers', pqrNumbers.join(','))
  }
  const query = params.toString() ? `?${params.toString()}` : ''
  const url = `${base}${query}`
  const res = await fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    }
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({} as { error?: string }))
    throw new ApiError(res.status, body.error || res.statusText)
  }

  const payload = (await res.json()) as WpsPreviewResponse
  return {
    data: payload.data,
    version: typeof payload.version === 'number' ? payload.version : 0
  }
}
